import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { apiUrl } from "@/lib/api-config";
import {
  ArrowLeft,
  Pencil,
  Printer,
  Copy,
  Send,
  Mail,
  MapPin,
  Truck,
  Weight,
  FileText,
  User,
  Clock,
  Tag,
  ChevronDown,
  Loader2,
  Package,
  AlertTriangle,
  StickyNote,
  X,
  CheckCircle2,
} from "lucide-react";

const DEFAULT_BOL_TO = [
  { name: "Luis Carrasco", email: "lcarrasco@soilseedandwater.com" },
  { name: "Johnathan Carrasco", email: "jcarrasco@soilseedandwater.com" },
];
const DEFAULT_BOL_CC = [
  { name: "Rodolfo Alvarez", email: "ralvarez@soilseedandwater.com" },
];

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Bol = {
  id: number;
  bol_number: string;
  date: string;
  status: string;
  client_tag: string | null;
  customer_name: string | null;
  reference_number: string | null;

  origin_location: string | null;
  origin_address: string | null;
  origin_city: string | null;
  origin_state: string | null;
  origin_zip: string | null;

  destination_address: string | null;
  destination_city: string | null;
  destination_state: string | null;
  destination_zip: string | null;
  onsite_contact_name: string | null;
  onsite_contact_phone: string | null;

  material_type: string | null;
  material_description: string | null;
  load_type: string | null;
  cargo_mode?: string | null;
  line_items?: Array<{
    quantity?: number;
    unit?: string;
    product?: string;
    notes?: string | null;
  }> | null;

  gross_weight: number | null;
  tare_weight: number | null;
  net_weight: number | null;
  net_weight_tons: number | null;

  carrier_name: string | null;
  driver_name: string | null;
  truck_number: string | null;
  license_plate: string | null;
  trailer_number: string | null;
  time_in: string | null;
  time_out: string | null;
  scale_operator_initials: string | null;

  notes: string | null;
  work_order_id: number | null;
  work_order_number: string | null;
  created_at: string;
  updated_at: string;
};

type LinkedCod = {
  id: number;
  cod_number: string;
};

/* ------------------------------------------------------------------ */
/*  Status badge config                                                */
/* ------------------------------------------------------------------ */

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  draft: {
    label: "Draft",
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-700 dark:text-yellow-400",
  },
  completed: {
    label: "Completed",
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-400",
  },
  delivered: {
    label: "Delivered",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-400",
  },
};

const CLIENT_TAG_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  vanguard: { label: "Vanguard", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  willcox: { label: "Willcox", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  "3lag": { label: "3LAG", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  flower_gods: { label: "Flower Gods", bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-700 dark:text-pink-400" },
};

const CLIENT_TAG_OPTIONS = [
  { value: "", label: "No tag" },
  { value: "vanguard", label: "Vanguard / Tyson" },
  { value: "willcox", label: "Willcox Pistachio" },
  { value: "3lag", label: "Jack / 3LAG" },
  { value: "flower_gods", label: "Flower of the Gods" },
];

const LOAD_TYPE_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  Outbound: { label: "Outbound", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  Inbound: { label: "Inbound", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  Transfer: { label: "Transfer", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" },
  Return: { label: "Return", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(dateStr: string | null) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime(timeStr: string | null) {
  if (!timeStr) return "-";
  if (timeStr.includes("T")) {
    return new Date(timeStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return timeStr;
}

function formatWeight(lbs: number | null): string {
  if (lbs === null || lbs === undefined) return "-";
  return `${lbs.toLocaleString()} lbs`;
}

function formatAddress(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(", ") || "-";
}

/* ------------------------------------------------------------------ */
/*  Badge component                                                    */
/* ------------------------------------------------------------------ */

function Badge({ config }: { config: { label: string; bg: string; text: string } }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Detail row component                                               */
/* ------------------------------------------------------------------ */

function DetailRow({
  label,
  value,
  mono,
  bold,
  valueColor,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  bold?: boolean;
  valueColor?: string;
}) {
  return (
    <div className="flex justify-between py-2 border-b border-border/50 last:border-0">
      <span className={`text-sm ${bold ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
        {label}
      </span>
      <span
        className={`text-sm text-right ${mono ? "font-mono" : ""} ${
          bold ? "font-bold" : "font-medium"
        } ${valueColor || "text-foreground"}`}
      >
        {value || "-"}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ViewBol() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [bol, setBol] = useState<Bol | null>(null);
  const [linkedCods, setLinkedCods] = useState<LinkedCod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [tagUpdating, setTagUpdating] = useState(false);

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyTo, setNotifyTo] = useState(DEFAULT_BOL_TO.map((r) => ({ ...r, checked: true })));
  const [notifyCc, setNotifyCc] = useState(DEFAULT_BOL_CC);
  const [notifyComment, setNotifyComment] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState<string | null>(null);

  /* -- Fetch BOL --------------------------------------------------- */

  const loadBol = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [bolRes, codsRes] = await Promise.all([
        apiFetch(`/api/ops/bols/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        apiFetch(`/api/ops/bols/${id}/cods`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!bolRes.ok) throw new Error(`Failed to load BOL (${bolRes.status})`);
      const bolData = await bolRes.json();
      setBol(bolData.bol || bolData);

      if (codsRes.ok) {
        const codsData = await codsRes.json();
        setLinkedCods(codsData.cods || []);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load BOL");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadBol();
  }, [loadBol]);

  /* -- Status update ----------------------------------------------- */

  const handleStatusChange = async (newStatus: string) => {
    if (!bol || newStatus === bol.status) return;
    setStatusUpdating(true);
    try {
      const res = await apiFetch(`/api/ops/bols/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      const data = await res.json();
      setBol(data.bol || { ...bol, status: newStatus });
    } catch {
      setError("Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  /* -- Client tag update ------------------------------------------- */

  const handleTagChange = async (newTag: string) => {
    if (!bol) return;
    const tagValue = newTag || null;
    if (tagValue === bol.client_tag) return;
    setTagUpdating(true);
    try {
      const res = await apiFetch(`/api/ops/bols/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ client_tag: tagValue }),
      });
      if (!res.ok) throw new Error("Failed to update client tag");
      const data = await res.json();
      setBol(data.bol || { ...bol, client_tag: tagValue });
    } catch {
      setError("Failed to update client tag");
    } finally {
      setTagUpdating(false);
    }
  };

  /* -- Action handlers --------------------------------------------- */

  const handlePrint = () => {
    const pdfUrl = apiUrl(`/api/ops/bols/${id}/pdf?token=${encodeURIComponent(token ?? "")}`);
    window.open(pdfUrl, "_blank");
  };

  const handleDuplicate = () => {
    navigate(`/app/ops/bols/new?duplicate=${id}`);
  };

  const openNotifyModal = async () => {
    setShowNotifyModal(true);
    setNotifyComment("");
    setNotifySuccess(null);
    try {
      const res = await apiFetch("/api/ops/bols/notify-recipients", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const to = (data.to?.length ? data.to : DEFAULT_BOL_TO).map((r: { name: string; email: string }) => ({
          ...r,
          checked: true,
        }));
        setNotifyTo(to);
        setNotifyCc(data.cc?.length ? data.cc : DEFAULT_BOL_CC);
      }
    } catch {
      setNotifyTo(DEFAULT_BOL_TO.map((r) => ({ ...r, checked: true })));
      setNotifyCc(DEFAULT_BOL_CC);
    }
  };

  const handleSendToOperations = async () => {
    if (notifying || !id) return;
    const teamEmails = notifyTo.filter((r) => r.checked).map((r) => r.email);
    if (teamEmails.length === 0) {
      setError("Select at least Luis or Johnathan before sending.");
      return;
    }
    setNotifying(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/ops/bols/${id}/notify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          teamEmails,
          ccEmails: notifyCc.map((r) => r.email),
          comment: notifyComment || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to email BOL");
      setNotifySuccess(`Sent to ${teamEmails.join(", ")}`);
      setTimeout(() => {
        setShowNotifyModal(false);
        setNotifySuccess(null);
      }, 1600);
    } catch (err: any) {
      setError(err?.message || "Failed to email BOL to operations");
    } finally {
      setNotifying(false);
    }
  };

  /* -- Loading state ----------------------------------------------- */

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-[600px] bg-card border border-border rounded-xl animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-xl p-5 animate-pulse"
              >
                <div className="h-4 bg-muted rounded w-24 mb-4" />
                <div className="space-y-3">
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* -- Error state ------------------------------------------------- */

  if (error && !bol) {
    return (
      <div className="max-w-7xl space-y-6">
        <Link
          to="/app/ops"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Operations
        </Link>
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <AlertTriangle
            size={32}
            className="mx-auto text-muted-foreground/30 mb-3"
          />
          <p className="text-sm text-red-600 dark:text-red-400">
            {error || "BOL not found"}
          </p>
        </div>
      </div>
    );
  }

  if (!bol) return null;

  const statusCfg = STATUS_CONFIG[bol.status] || STATUS_CONFIG.draft;
  const clientCfg = bol.client_tag ? CLIENT_TAG_CONFIG[bol.client_tag] : null;
  const loadCfg = bol.load_type ? LOAD_TYPE_CONFIG[bol.load_type] : null;

  return (
    <div className="space-y-6 max-w-7xl">
      {/* ---- Header ---- */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/app/ops"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <ArrowLeft size={14} />
            Back to Operations
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="heading-display text-3xl text-foreground">
              BOL #{bol.bol_number}
            </h1>
            <Badge config={statusCfg} />
            {clientCfg && <Badge config={clientCfg} />}
            {loadCfg && <Badge config={loadCfg} />}
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            {formatDate(bol.date)}
            {bol.customer_name && (
              <>
                <span className="text-muted-foreground/40 mx-1.5">&middot;</span>
                <span className="font-medium text-foreground">{bol.customer_name}</span>
              </>
            )}
            {bol.reference_number && (
              <>
                <span className="text-muted-foreground/40 mx-1.5">&middot;</span>
                <span className="font-mono text-xs">Ref: {bol.reference_number}</span>
              </>
            )}
            {bol.work_order_id && bol.work_order_number && (
              <>
                <span className="text-muted-foreground/40 mx-1.5">&middot;</span>
                <button
                  onClick={() => navigate(`/app/ops/work-orders/${bol.work_order_id}`)}
                  className="font-mono text-xs text-forest-600 dark:text-forest-400 hover:underline"
                >
                  Job: {bol.work_order_number}
                </button>
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate(`/app/ops/inventory?bolId=${id}`)} title="Review stock and explicitly record a dispatch. Saving a BOL does not deduct inventory." className="inline-flex items-center gap-2 px-4 py-2.5 bg-card hover:bg-secondary text-sm font-semibold rounded-xl border border-border">Inventory</button>
          <button
            onClick={() => navigate(`/app/ops/bols/${id}/edit`)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Pencil size={14} />
            Edit
          </button>
          <button
            onClick={openNotifyModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-amber-600/20"
          >
            <Send size={14} />
            Send to Operations
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-card hover:bg-secondary text-foreground text-sm font-semibold rounded-xl border border-border transition-colors"
          >
            <Printer size={14} />
            Print PDF
          </button>
          <button
            onClick={handleDuplicate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-card hover:bg-secondary text-foreground text-sm font-semibold rounded-xl border border-border transition-colors"
          >
            <Copy size={14} />
            Duplicate
          </button>
        </div>
      </div>

      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Send size={18} className="text-amber-600" />
                Email BOL to Operations
              </h2>
              <button
                onClick={() => setShowNotifyModal(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Send to</h3>
                <div className="space-y-2">
                  {notifyTo.map((r) => (
                    <label
                      key={r.email}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={r.checked}
                        onChange={(e) =>
                          setNotifyTo((prev) =>
                            prev.map((m) =>
                              m.email === r.email ? { ...m, checked: e.target.checked } : m
                            )
                          )
                        }
                        className="h-4 w-4 rounded border-border text-forest-600 focus:ring-forest-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Mail size={14} />
                  Copy (CC)
                </h3>
                <div className="space-y-2">
                  {notifyCc.map((r) => (
                    <div
                      key={r.email}
                      className="flex items-center gap-3 p-2.5 rounded-lg bg-secondary/40 border border-border"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">CC</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Comment (optional)</label>
                <textarea
                  value={notifyComment}
                  onChange={(e) => setNotifyComment(e.target.value)}
                  rows={3}
                  placeholder="Note for operations…"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-xs text-muted-foreground">
                  {notifyTo.filter((r) => r.checked).length} recipient(s) + {notifyCc.length} CC · PDF attached
                </p>
                <button
                  onClick={handleSendToOperations}
                  disabled={notifying || notifyTo.filter((r) => r.checked).length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl"
                >
                  {notifySuccess ? (
                    <>
                      <CheckCircle2 size={14} />
                      {notifySuccess}
                    </>
                  ) : notifying ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Two-column layout ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: PDF preview */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <FileText size={15} className="text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">
              PDF Preview
            </span>
          </div>
          <iframe
            src={apiUrl(`/api/ops/bols/${id}/pdf?token=${encodeURIComponent(token ?? "")}`)}
            title={`BOL #${bol.bol_number} PDF`}
            className="w-full h-[700px] bg-white"
          />
        </div>

        {/* Right: Detail cards */}
        <div className="space-y-4">
          {/* Status & Tag management */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
              <Tag size={14} className="text-forest-600 dark:text-forest-400" />
              Status &amp; Classification
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Status
                </label>
                <div className="relative">
                  <select
                    value={bol.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusUpdating}
                    className="w-full appearance-none px-3 py-2.5 bg-background border border-border rounded-lg text-sm pr-8 disabled:opacity-50 text-foreground"
                  >
                    <option value="draft">Draft</option>
                    <option value="completed">Completed</option>
                    <option value="delivered">Delivered</option>
                  </select>
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    {statusUpdating ? (
                      <Loader2 size={14} className="animate-spin text-muted-foreground" />
                    ) : (
                      <ChevronDown size={14} className="text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Client Tag
                </label>
                <div className="relative">
                  <select
                    value={bol.client_tag || ""}
                    onChange={(e) => handleTagChange(e.target.value)}
                    disabled={tagUpdating}
                    className="w-full appearance-none px-3 py-2.5 bg-background border border-border rounded-lg text-sm pr-8 disabled:opacity-50 text-foreground"
                  >
                    {CLIENT_TAG_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    {tagUpdating ? (
                      <Loader2 size={14} className="animate-spin text-muted-foreground" />
                    ) : (
                      <ChevronDown size={14} className="text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Origin */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <MapPin size={14} className="text-forest-600 dark:text-forest-400" />
              Origin
            </h3>
            <DetailRow label="Facility" value={bol.origin_location} />
            <DetailRow
              label="Address"
              value={formatAddress([
                bol.origin_address,
                bol.origin_city,
                bol.origin_state,
                bol.origin_zip,
              ])}
            />
          </div>

          {/* Destination */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <MapPin size={14} className="text-blue-600 dark:text-blue-400" />
              Destination
            </h3>
            <DetailRow
              label="Address"
              value={formatAddress([
                bol.destination_address,
                bol.destination_city,
                bol.destination_state,
                bol.destination_zip,
              ])}
            />
            <DetailRow label="Contact" value={[bol.onsite_contact_name, bol.onsite_contact_phone].filter(Boolean).join(" - ") || null} />
          </div>

          {/* Material */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <Package size={14} className="text-amber-600 dark:text-amber-400" />
              Material
              {String(bol.cargo_mode || "").toLowerCase() === "flatbed" && (
                <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                  Flatbed
                </span>
              )}
            </h3>
            <DetailRow label="Type" value={bol.material_type} />
            {String(bol.cargo_mode || "").toLowerCase() === "flatbed" &&
            Array.isArray(bol.line_items) &&
            bol.line_items.length > 0 ? (
              <div className="mt-2 mb-3 overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-secondary/60 text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="text-left font-semibold px-3 py-2">Qty</th>
                      <th className="text-left font-semibold px-3 py-2">Unit</th>
                      <th className="text-left font-semibold px-3 py-2">Product / Item</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bol.line_items.map((item, idx) => (
                      <tr key={`${item.product}-${idx}`} className="border-t border-border">
                        <td className="px-3 py-2 font-semibold tabular-nums">{item.quantity ?? "—"}</td>
                        <td className="px-3 py-2 capitalize text-muted-foreground">{item.unit || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground">{item.product || "—"}</div>
                          {item.notes ? (
                            <div className="text-xs text-muted-foreground mt-0.5">{item.notes}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <DetailRow label="Description" value={bol.material_description} />
            )}
            <DetailRow label="Load Type" value={bol.load_type} />
          </div>

          {/* Tonnage highlight */}
          {bol.net_weight_tons != null && Number(bol.net_weight_tons) > 0 && (
            <div className="bg-forest-600 dark:bg-forest-700 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">Net Tonnage</p>
                  <p className="text-3xl font-bold tabular-nums mt-1">
                    {Number(bol.net_weight_tons).toFixed(2)} <span className="text-lg font-semibold text-white/80">tons</span>
                  </p>
                </div>
                <Weight size={32} className="text-white/30" />
              </div>
              <div className="mt-3 pt-3 border-t border-white/20 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">Gross</p>
                  <p className="text-sm font-bold tabular-nums">{bol.gross_weight != null ? Number(bol.gross_weight).toLocaleString() : "-"} <span className="text-[10px] text-white/70">lbs</span></p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">Tare</p>
                  <p className="text-sm font-bold tabular-nums">{bol.tare_weight != null ? Number(bol.tare_weight).toLocaleString() : "-"} <span className="text-[10px] text-white/70">lbs</span></p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">Net</p>
                  <p className="text-sm font-bold tabular-nums">{bol.net_weight != null ? Number(bol.net_weight).toLocaleString() : "-"} <span className="text-[10px] text-white/70">lbs</span></p>
                </div>
              </div>
            </div>
          )}

          {/* Weight (fallback if no tonnage) */}
          {(bol.net_weight_tons == null || Number(bol.net_weight_tons) === 0) && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <Weight size={14} className="text-amber-600 dark:text-amber-400" />
                Weight
              </h3>
              <DetailRow label="Gross" value={formatWeight(bol.gross_weight)} />
              <DetailRow label="Tare" value={formatWeight(bol.tare_weight)} />
              <DetailRow label="Net" value={formatWeight(bol.net_weight)} bold />
            </div>
          )}

          {/* Carrier / Transport */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <Truck size={14} className="text-blue-600 dark:text-blue-400" />
              Carrier / Transport
            </h3>
            <DetailRow label="Carrier" value={bol.carrier_name} />
            <DetailRow label="Driver" value={bol.driver_name} />
            <DetailRow label="Truck #" value={bol.truck_number} mono />
            <DetailRow label="License Plate" value={bol.license_plate} mono />
            <DetailRow label="Trailer #" value={bol.trailer_number} mono />
          </div>

          {/* Timing */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <Clock size={14} className="text-forest-600 dark:text-forest-400" />
              Timing
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                  Time In
                </span>
                <span className="text-sm text-foreground font-medium flex items-center gap-1">
                  <Clock size={12} className="text-muted-foreground" />
                  {formatTime(bol.time_in)}
                </span>
              </div>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                  Time Out
                </span>
                <span className="text-sm text-foreground font-medium flex items-center gap-1">
                  <Clock size={12} className="text-muted-foreground" />
                  {formatTime(bol.time_out)}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border/50">
              <DetailRow label="Scale Operator" value={bol.scale_operator_initials} />
            </div>
          </div>

          {/* Notes */}
          {bol.notes && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <StickyNote size={14} className="text-muted-foreground" />
                Notes
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {bol.notes}
              </p>
            </div>
          )}

          {/* Linked CODs */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <FileText size={14} className="text-purple-600 dark:text-purple-400" />
              Linked CODs
            </h3>
            {linkedCods.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No CODs linked to this BOL.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {linkedCods.map((cod) => (
                  <Link
                    key={cod.id}
                    to={`/app/ops/cods/${cod.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors border border-purple-200 dark:border-purple-800"
                  >
                    <FileText size={12} />
                    COD #{cod.cod_number}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <User size={14} className="text-muted-foreground" />
              Record Info
            </h3>
            <DetailRow label="Created" value={formatDateTime(bol.created_at)} />
            <DetailRow label="Last Updated" value={formatDateTime(bol.updated_at)} />
          </div>
        </div>
      </div>
    </div>
  );
}
